import { useEffect, useState, useRef } from "react";
import { useSearchParams, Link } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { CheckCircle2, AlertCircle, Clock, ArrowRight } from "lucide-react";

export default function BillingSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const { refresh } = useAuth();
  const [state, setState] = useState({ status: "checking", payment_status: null });
  const attemptsRef = useRef(0);

  useEffect(() => {
    if (!sessionId) {
      setState({ status: "error", payment_status: "missing_session" });
      return;
    }
    const poll = async () => {
      try {
        const { data } = await api.get(`/billing/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setState({ status: "paid", ...data });
          await refresh();
          return;
        }
        if (data.status === "expired") {
          setState({ status: "expired", ...data });
          return;
        }
        attemptsRef.current += 1;
        if (attemptsRef.current >= 6) {
          setState({ status: "timeout", ...data });
          return;
        }
        setTimeout(poll, 2000);
      } catch {
        setState({ status: "error", payment_status: "request_failed" });
      }
    };
    poll();
  }, [sessionId, refresh]);

  return (
    <div className="min-h-screen bg-[#FAFAFA] flex items-center justify-center p-6" data-testid="billing-success-page">
      <div className="max-w-lg w-full bg-white border border-zinc-200 rounded-sm p-10 text-center">
        {state.status === "checking" && (
          <>
            <div className="w-10 h-10 border-2 border-zinc-200 border-t-black rounded-full animate-spin mx-auto mb-4"/>
            <h1 className="font-display text-2xl font-medium tracking-tight">Vérification du paiement...</h1>
            <p className="text-sm text-zinc-500 mt-2">Cela ne prend que quelques secondes.</p>
          </>
        )}
        {state.status === "paid" && (
          <>
            <CheckCircle2 size={40} className="klein-text mx-auto mb-4"/>
            <h1 className="font-display text-3xl font-medium tracking-tight mb-2">Paiement confirmé</h1>
            <p className="text-zinc-600 mb-6">Votre abonnement est activé. Un email de confirmation est en route.</p>
            <Link to="/app/dashboard">
              <Button className="rounded-sm bg-black text-white hover:bg-zinc-800" data-testid="billing-go-dashboard">
                Accéder au tableau de bord <ArrowRight size={14} className="ml-2"/>
              </Button>
            </Link>
          </>
        )}
        {state.status === "timeout" && (
          <>
            <Clock size={40} className="text-zinc-500 mx-auto mb-4"/>
            <h1 className="font-display text-2xl font-medium tracking-tight mb-2">Paiement en cours de traitement</h1>
            <p className="text-zinc-600">Nous vous enverrons un email dès que votre paiement sera confirmé.</p>
          </>
        )}
        {(state.status === "error" || state.status === "expired") && (
          <>
            <AlertCircle size={40} className="text-red-600 mx-auto mb-4"/>
            <h1 className="font-display text-2xl font-medium tracking-tight mb-2">Paiement non finalisé</h1>
            <p className="text-zinc-600 mb-6">{state.status === "expired" ? "La session a expiré." : "Une erreur est survenue."}</p>
            <Link to="/#pricing">
              <Button variant="outline" className="rounded-sm">Réessayer</Button>
            </Link>
          </>
        )}
      </div>
    </div>
  );
}
